// Dialog: Add Invoice

// Invoice upload & AI extraction flow (Steps 1–2 of the 3-step process).
//
// Flow:
//   Step 1: Upload Invoice — uploads to bldt-ai-fn (bronze container),
//           kicks off Document Intelligence pipeline, creates DRAFT invoice.
//           User can also enter fields manually alongside the upload.
//   Step 2: AI Extraction — listens for SSE docAI events, fetches silver blob.
//           Shows side-by-side comparison of user-entered vs AI-extracted values.
//           User picks which values to keep, then "Complete Upload" saves.
//
// Steps 3 (Team Verification) and 4 (Payment) happen on PageInvoice.

// react
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// mui
import {
	alpha,
	Avatar,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	FormControlLabel,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Radio,
	RadioGroup,
	Select,
	Stack,
	Step,
	StepLabel,
	Stepper,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	ArrowBack as IconBack,
	ArrowForward as IconNext,
	AutoAwesome as IconAI,
	CheckCircle as IconCheckCircle,
	Close as IconClose,
	CloudUpload as IconUpload,
	DeleteForever as IconDelete,
	Info as IconInfo,
	Receipt as IconInvoice,
	Warning as IconWarning,
} from '@mui/icons-material';

// design system
import { radius } from '../../../utils/designSystem';

// context
import { ContextBanner } from '../../../App.context';
import { ContextSpendPlan } from '../../pages/ProjectV2/ProjectV2.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// utils
import { defaultCurrencies } from '../../../utils/defaults';

// ai services
import { uploadAiDocument, updateAiDocument, fetchAiBlob } from '../../../services/aiApi';
import { useSSE } from '../../../utils/sse/useSSE';

// types
import { InvoicePayload, Invoice } from '../../../@types';
import type { AiUploadResponse } from '../../pages/ProjectV2/Documents/documentTypes';

// Props

export interface DialogAddInvoiceProps {
	contractId: number | null;
	contractNum: string;
	supplierName: string;
	contractCurrency: string;
	contractAmount: number;
	milestones: { id: number; name: string; percentage: number; amount: number }[];
	projectId?: number;
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	draftInvoice?: Invoice | null;
	/** When true, opens in edit mode — skips upload, preserves existing status on save */
	editMode?: boolean;
}

// Step definitions

const STEP_KEYS = ['invoice.step.upload', 'invoice.step.aiExtraction'];

// Extracted field from AI silver blob / manual entry
interface ExtractedField {
	label: string;
	extracted: string;
	found: boolean;
}

// Parsed silver blob result for sharing between SSE handler and DRAFT resume
interface SilverBlobParsed {
	fields: ExtractedField[];
	foundCount: number;
	invoiceNumber: string;
	invoiceDate: string;
	totalAmount: number;
	currency: string;
	dueDate: string;
}

/** Parse raw silver blob content into extracted fields + pre-fill values */
const parseSilverBlobContent = (
	content: Record<string, unknown>,
	fallbackCurrency: string
): SilverBlobParsed => {
	const pages = Array.isArray(content) ? content : [content];
	const page = (pages[0] as Record<string, unknown>) || {};
	const gfe = (page.general_field_extraction as Record<string, unknown>) || {};
	const fv = (page.field_verification as Record<string, unknown>) || {};

	const parseDollar = (v: unknown): number => {
		if (typeof v === 'number') return v;
		if (typeof v !== 'string') return 0;
		const cleaned = v.replace(/[^0-9.]/g, '');
		return parseFloat(cleaned) || 0;
	};
	const isBlank = (v: string) => !v || v === 'Not Found' || v === 'Not Provided';

	const vendorName = String(
		gfe['Vendor'] ||
			gfe['Supplier'] ||
			gfe['vendor'] ||
			gfe['supplier_name'] ||
			fv['supplier_name'] ||
			page.vendor ||
			page.vendor_name ||
			''
	);
	const cleanVendor = isBlank(vendorName) ? '' : vendorName;
	const totalAmount = parseDollar(
		gfe['Amount Due This Invoice'] ||
			gfe['Total Amount'] ||
			gfe['Invoice Amount'] ||
			gfe['amount'] ||
			fv['amount'] ||
			page.amount ||
			page.total_amount ||
			0
	);
	const rawCurrency = String(gfe['Currency'] || gfe['currency'] || page.currency || '');
	const amountStr = String(
		gfe['Amount Due This Invoice'] || gfe['Total Amount'] || gfe['Contract Amount'] || ''
	);
	const extractedCurrency = rawCurrency || (amountStr.includes('$') ? 'USD' : '');
	const extractedDate = String(
		gfe['Date'] || gfe['Invoice Date'] || gfe['date'] || page.date || page.invoice_date || ''
	);
	const extractedInvNum = String(
		gfe['Invoice Number'] ||
			gfe['Invoice No'] ||
			gfe['invoice_number'] ||
			fv['invoice_number'] ||
			page.invoice_number ||
			''
	);
	const cleanInvNum = isBlank(extractedInvNum) ? '' : extractedInvNum;
	const extractedDueDate = String(
		gfe['Due Date'] || gfe['due_date'] || fv['due_date'] || page.due_date || ''
	);
	const cleanDueDate = isBlank(extractedDueDate) ? '' : extractedDueDate;

	const extractedDocType = String(page.document_type || gfe['Document Type'] || '');
	const extractedBillingType = String(
		gfe['Contract Billing Type'] || gfe['Billing Type'] || gfe['billing_type'] || ''
	);
	const extractedProject = String(gfe['Project'] || gfe['project'] || '');
	const extractedContractAmt = parseDollar(gfe['Contract Amount'] || gfe['contract_amount'] || 0);
	const extractedProgress = String(gfe['Progress %'] || gfe['progress'] || '');
	const summary = String(page.summary || '');
	const cur = extractedCurrency || fallbackCurrency;

	const fields: ExtractedField[] = [
		{ label: 'Supplier Name', extracted: cleanVendor || '', found: !!cleanVendor },
		{ label: 'Document Type', extracted: extractedDocType || '', found: !!extractedDocType },
		{ label: 'Billing Type', extracted: extractedBillingType || '', found: !!extractedBillingType },
		{ label: 'Project', extracted: extractedProject || '', found: !!extractedProject },
		{
			label: 'Contract Amount',
			extracted: extractedContractAmt ? `${cur} ${extractedContractAmt.toLocaleString()}` : '',
			found: !!extractedContractAmt,
		},
		{ label: 'Progress', extracted: extractedProgress || '', found: !!extractedProgress },
		{ label: 'Invoice Number', extracted: cleanInvNum || '', found: !!cleanInvNum },
		{
			label: 'Invoice Amount',
			extracted: totalAmount ? `${cur} ${totalAmount.toLocaleString()}` : '',
			found: !!totalAmount,
		},
		{ label: 'Invoice Date', extracted: extractedDate || '', found: !!extractedDate },
		{ label: 'Currency', extracted: extractedCurrency || '', found: !!extractedCurrency },
		{ label: 'Due Date', extracted: cleanDueDate || '', found: !!cleanDueDate },
		{ label: 'Summary', extracted: summary || '', found: !!summary },
	];

	return {
		fields,
		foundCount: fields.filter((f) => f.found).length,
		invoiceNumber: cleanInvNum,
		invoiceDate: extractedDate,
		totalAmount,
		currency: extractedCurrency,
		dueDate: cleanDueDate,
	};
};

/** Normalise any date string to YYYY-MM-DD for HTML date inputs */
const toInputDate = (v: string | null | undefined): string => {
	if (!v) return '';
	// Already YYYY-MM-DD?
	if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
	// Try parsing with Date (handles ISO 8601, MM/DD/YYYY, etc.)
	const d = new Date(v);
	if (Number.isNaN(d.getTime())) return '';
	const yyyy = d.getFullYear();
	const mm = String(d.getMonth() + 1).padStart(2, '0');
	const dd = String(d.getDate()).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
};

// Component

export const DialogAddInvoice = (props: DialogAddInvoiceProps) => {
	const {
		contractId,
		contractNum,
		supplierName,
		contractCurrency,
		contractAmount,
		milestones,
		projectId,
		isOpen,
		onClose,
		onSuccess,
		draftInvoice,
		editMode = false,
	} = props;

	const { t } = useTranslation();
	const theme = useTheme();
	const { createInvoice, updateInvoice, deactivateInvoice } = useContext(ContextSpendPlan);
	const { setBanner } = useContext(ContextBanner);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// Fetch the full currency list from the API; fall back to defaults if it
	// fails or is unavailable. Mirrors the behaviour of <SelectCurrency/>.
	useFetch({
		url: `${process.env.REACT_APP_API_URL}/currencies`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (Array.isArray(res?.data) && res.data.length) {
				setCurrencies(res.data);
			}
		},
	});

	// =========================================================================
	// Stepper
	// =========================================================================
	const [activeStep, setActiveStep] = useState(0);

	// =========================================================================
	// Step 1: Upload Invoice
	// =========================================================================
	const [invoiceNumber, setInvoiceNumber] = useState('');
	const [invoiceDate, setInvoiceDate] = useState('');
	const [invoiceAmount, setInvoiceAmount] = useState('');
	const [invoiceCurrency, setInvoiceCurrency] = useState(contractCurrency || 'USD');
	const [currencies, setCurrencies] = useState<string[]>(defaultCurrencies);
	const [dueDate, setDueDate] = useState('');
	const [selectedMilestone, setSelectedMilestone] = useState<number | ''>('');
	const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
	const [invoiceDescription, setInvoiceDescription] = useState('');

	// Upload state (bldt-ai-fn bronze)
	const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>(
		'idle'
	);
	const [uploadResult, setUploadResult] = useState<AiUploadResponse | null>(null);
	const [uploadError, setUploadError] = useState<string | null>(null);

	// =========================================================================
	// Step 2: AI Extraction (SSE-driven from silver blob)
	// =========================================================================
	const [aiExtracting, setAiExtracting] = useState(false);
	const [aiExtracted, setAiExtracted] = useState(false);
	const [aiConfidence, setAiConfidence] = useState(0);
	const [aiFields, setAiFields] = useState<ExtractedField[]>([]);
	const [silverBlobData, setSilverBlobData] = useState<Record<string, unknown> | null>(null);
	const jobIdRef = useRef<string | null>(null);

	// =========================================================================
	// Completion state
	// =========================================================================
	const [, setIsSubmitting] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [createdInvoiceId, setCreatedInvoiceId] = useState<number | null>(null);
	const [skipAiExtraction, setSkipAiExtraction] = useState(false);
	const [abandonConfirmOpen, setAbandonConfirmOpen] = useState(false);
	const [isAbandoning, setIsAbandoning] = useState(false);
	const hydratedDraftInvoiceIdRef = useRef<number | null>(null);

	// =========================================================================
	// Field merge: track user-entered vs AI-extracted values
	// =========================================================================
	type FieldSource = 'user' | 'ai' | 'custom';
	const MERGE_FIELD_KEYS = [
		'invoiceNumber',
		'invoiceDate',
		'invoiceAmount',
		'invoiceCurrency',
		'dueDate',
	] as const;
	type MergeFieldKey = (typeof MERGE_FIELD_KEYS)[number];

	const [userEnteredFields, setUserEnteredFields] = useState<Record<MergeFieldKey, string>>({
		invoiceNumber: '',
		invoiceDate: '',
		invoiceAmount: '',
		invoiceCurrency: '',
		dueDate: '',
	});
	const [aiExtractedValues, setAiExtractedValues] = useState<Record<MergeFieldKey, string>>({
		invoiceNumber: '',
		invoiceDate: '',
		invoiceAmount: '',
		invoiceCurrency: '',
		dueDate: '',
	});
	const [fieldSources, setFieldSources] = useState<Record<MergeFieldKey, FieldSource>>({
		invoiceNumber: 'ai',
		invoiceDate: 'ai',
		invoiceAmount: 'ai',
		invoiceCurrency: 'ai',
		dueDate: 'ai',
	});
	const [customFields, setCustomFields] = useState<Record<MergeFieldKey, string>>({
		invoiceNumber: '',
		invoiceDate: '',
		invoiceAmount: '',
		invoiceCurrency: '',
		dueDate: '',
	});

	// =========================================================================
	// Resume from DRAFT invoice
	// =========================================================================

	// Fallback: reconstruct AI fields from the stored aiExtraction record
	// (used when the silver blob is no longer available)
	const restoreFromAiExtraction = (draft: Invoice) => {
		const ext = draft.aiExtraction;
		if (!ext) return;
		const cur = ext.currency || draft.currency || contractCurrency || 'USD';
		const fields: ExtractedField[] = [
			{ label: 'Supplier Name', extracted: ext.vendorName || '', found: !!ext.vendorName },
			{ label: 'Document Type', extracted: '', found: false },
			{ label: 'Billing Type', extracted: '', found: false },
			{ label: 'Project', extracted: '', found: false },
			{ label: 'Contract Amount', extracted: '', found: false },
			{ label: 'Progress', extracted: '', found: false },
			{ label: 'Invoice Number', extracted: ext.invoiceNumber || '', found: !!ext.invoiceNumber },
			{
				label: 'Invoice Amount',
				extracted: ext.totalAmount ? `${cur} ${ext.totalAmount.toLocaleString()}` : '',
				found: !!ext.totalAmount,
			},
			{ label: 'Invoice Date', extracted: ext.invoiceDate || '', found: !!ext.invoiceDate },
			{ label: 'Currency', extracted: ext.currency || '', found: !!ext.currency },
			{ label: 'Due Date', extracted: ext.dueDate || '', found: !!ext.dueDate },
			{ label: 'Summary', extracted: '', found: false },
		];
		setAiFields(fields);
		setAiConfidence(fields.filter((f) => f.found).length);
		setAiExtracted(true);
		setAiExtracting(false);
	};

	useEffect(() => {
		if (!isOpen) {
			hydratedDraftInvoiceIdRef.current = null;
			return;
		}
		if (!draftInvoice) return;

		if (hydratedDraftInvoiceIdRef.current === draftInvoice.id) return;
		hydratedDraftInvoiceIdRef.current = draftInvoice.id;

		// Restore basic form state from the saved DRAFT
		setCreatedInvoiceId(draftInvoice.id);
		setInvoiceNumber(draftInvoice.num || '');
		setInvoiceDate(toInputDate(draftInvoice.date));
		setDueDate(toInputDate(draftInvoice.dateDue));
		setInvoiceAmount(draftInvoice.amount != null ? String(draftInvoice.amount) : '');
		setInvoiceCurrency(draftInvoice.currency || contractCurrency || 'USD');
		setInvoiceDescription(draftInvoice.description || '');
		setSelectedMilestone(
			draftInvoice.milestoneNumber != null ? Number(draftInvoice.milestoneNumber) : ''
		);
		if (draftInvoice.attachments && draftInvoice.attachments.length > 0) {
			setUploadStatus('success');
		}

		// Start at Step 1 (AI Extraction / Review) since Step 0 (upload) is done
		setActiveStep(1);

		// Restore AI extraction state — always try the silver blob first (has full context),
		// then fall back to reconstructing from the saved aiExtraction record
		if (draftInvoice.attachments && draftInvoice.attachments.length > 0) {
			const attachment = draftInvoice.attachments[0];
			const baseName = attachment.name.replace(/\.[^/.]+$/, '');
			const silverBlobPath = `${baseName}-output.json`;

			setAiExtracting(true);
			fetchAiBlob('silver', silverBlobPath)
				.then((blobResponse) => {
					const content =
						typeof blobResponse.content === 'object'
							? (blobResponse.content as Record<string, unknown>)
							: {};
					setSilverBlobData(content);

					const parsed = parseSilverBlobContent(
						content,
						draftInvoice.currency || contractCurrency || 'USD'
					);
					setAiFields(parsed.fields);
					setAiConfidence(parsed.foundCount);
					setAiExtracted(true);
					setAiExtracting(false);

					// Populate AI extracted values for merge UI
					setAiExtractedValues({
						invoiceNumber: parsed.invoiceNumber || '',
						invoiceDate: toInputDate(parsed.invoiceDate) || '',
						invoiceAmount: parsed.totalAmount ? String(parsed.totalAmount) : '',
						invoiceCurrency: parsed.currency || '',
						dueDate: toInputDate(parsed.dueDate) || '',
					});

					// Pre-fill form fields from the fetched blob (only overwrite blanks)
					if (parsed.invoiceNumber && !draftInvoice.num) setInvoiceNumber(parsed.invoiceNumber);
					if (parsed.invoiceDate && !draftInvoice.date)
						setInvoiceDate(toInputDate(parsed.invoiceDate));
					if (parsed.totalAmount && draftInvoice.amount == null)
						setInvoiceAmount(String(parsed.totalAmount));
					if (parsed.currency) setInvoiceCurrency(parsed.currency);
					if (parsed.dueDate && !draftInvoice.dateDue) setDueDate(toInputDate(parsed.dueDate));
				})
				.catch(() => {
					// Silver blob not available — fall back to aiExtraction if present
					setAiExtracting(false);
					if (draftInvoice.aiExtraction) {
						restoreFromAiExtraction(draftInvoice);
					}
				});
		} else if (draftInvoice.aiExtraction) {
			restoreFromAiExtraction(draftInvoice);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- restoreFromAiExtraction is a stable local helper
	}, [isOpen, draftInvoice, contractCurrency]);

	// =========================================================================
	// Validation per step
	// =========================================================================
	// Step 1: always need a milestone. File or manual fields are optional here
	// (user can provide values alongside upload for merge in Step 2).
	const isStep1Valid = selectedMilestone !== '';

	// Step 2 valid when AI is done (or skipped/no file) AND core fields are filled
	const isStep2Valid =
		(aiExtracted || !uploadResult || !aiExtracting) &&
		invoiceNumber.trim().length > 0 &&
		invoiceDate.length > 0 &&
		invoiceAmount.length > 0;

	// =========================================================================
	// Handlers
	// =========================================================================

	const handleNext = async () => {
		if (activeStep >= STEP_KEYS.length - 1) return;
		setIsSaving(true);

		try {
			// Step 0 → 1 (no-file path): Create DRAFT invoice with manually entered fields
			if (activeStep === 0 && !createdInvoiceId && contractId) {
				const draftPayload: InvoicePayload = {
					contract: contractId,
					num: invoiceNumber || null,
					date: invoiceDate || null,
					dateDue: dueDate || null,
					amount: parseFloat(invoiceAmount) || null,
					currency: invoiceCurrency || contractCurrency || 'USD',
					status: 'DRAFT',
					source: 'MANUAL_ENTRY',
					milestoneNumber: selectedMilestone || null,
					milestoneName: milestones.find((m) => m.id === selectedMilestone)?.name || null,
				};
				const response = await createInvoice(draftPayload);
				if (response?.data?.id) {
					setCreatedInvoiceId(response.data.id);
				} else {
					console.error('DRAFT creation failed:', response?.error);
					return;
				}
				handleManualValidation();
			} else if (activeStep === 0 && createdInvoiceId) {
				// Resuming — PATCH milestone / description if changed
				await updateInvoice(createdInvoiceId, {
					milestoneNumber: selectedMilestone || null,
					milestoneName: milestones.find((m) => m.id === selectedMilestone)?.name || null,
					description: invoiceDescription || null,
				});
			}

			// Snapshot user-entered values before moving to Step 2 (merge screen)
			if (activeStep === 0) {
				setUserEnteredFields({
					invoiceNumber,
					invoiceDate,
					invoiceAmount,
					invoiceCurrency,
					dueDate,
				});
			}

			setActiveStep((prev) => prev + 1);
		} catch (err) {
			console.error('Failed to save invoice:', err);
		} finally {
			setIsSaving(false);
		}
	};

	const handleBack = () => {
		if (activeStep > 0) {
			setActiveStep((prev) => prev - 1);
		}
	};

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			setInvoiceFile(e.target.files[0]);
		}
	};

	// Upload file to bldt-ai-fn (bronze container), then create DRAFT invoice
	const handleUploadFile = useCallback(async () => {
		if (!invoiceFile || !contractId) return;
		setIsSaving(true);
		setUploadStatus('uploading');
		setUploadError(null);
		try {
			const result = await uploadAiDocument({
				file: invoiceFile,
				doc_type: 'invoice',
				contract_id: contractId ? String(contractId) : undefined,
				project_id: projectId,
				source_type: 'invoice',
				tag: contractId ? `contract:${contractId}` : undefined,
			});
			setUploadResult(result);
			setUploadStatus('success');
			jobIdRef.current = result.job_id;

			// Create DRAFT invoice immediately so the upload is linked
			if (!createdInvoiceId) {
				const draftPayload: InvoicePayload = {
					contract: contractId,
					num: invoiceNumber || null,
					date: invoiceDate || null,
					dateDue: dueDate || null,
					amount: parseFloat(invoiceAmount) || null,
					currency: invoiceCurrency || contractCurrency || 'USD',
					description: invoiceDescription || null,
					status: 'DRAFT',
					source: 'UPLOAD',
					milestoneNumber: selectedMilestone || null,
					milestoneName: milestones.find((m) => m.id === selectedMilestone)?.name || null,
					attachments: [
						{
							id: Number(result.document_id) || 0,
							name: invoiceFile.name,
							type: invoiceFile.type,
							size: invoiceFile.size,
							uploadedAt: new Date().toISOString(),
							uploadedBy: result.user_email || '',
						},
					],
				};
				const response = await createInvoice(draftPayload);
				if (response?.data?.id) {
					setCreatedInvoiceId(response.data.id);
					// Back-fill ai.document with invoice source_id and structured tags
					if (result.document_id) {
						updateAiDocument({
							id: result.document_id,
							source_type: 'invoice',
							source_id: response.data.id,
							tags: [
								`invoice:${response.data.id}`,
								...(contractId ? [`contract:${contractId}`] : []),
							],
						}).catch((err) => console.warn('Failed to update document metadata:', err));
					}
				} else {
					console.error('DRAFT creation failed:', response?.error);
					setUploadStatus('error');
					setUploadError(response?.error?.message || 'Failed to create draft invoice');
					return;
				}
			}

			// Snapshot user-entered values before auto-advancing to merge screen
			setUserEnteredFields({
				invoiceNumber,
				invoiceDate,
				invoiceAmount,
				invoiceCurrency,
				dueDate,
			});

			// Auto-advance to Step 2 where SSE listener picks up silver
			setAiExtracting(true);
			setActiveStep(1);
		} catch (err) {
			setUploadStatus('error');
			setUploadError(err instanceof Error ? err.message : 'Upload failed');
			// Advance to Step 2 anyway — user can enter details manually
			setActiveStep(1);
		} finally {
			setIsSaving(false);
		}
	}, [
		invoiceFile,
		contractId,
		projectId,
		createdInvoiceId,
		invoiceNumber,
		invoiceDate,
		invoiceAmount,
		invoiceCurrency,
		invoiceDescription,
		dueDate,
		contractCurrency,
		supplierName,
		selectedMilestone,
		milestones,
		createInvoice,
	]);

	// SSE listener for docAI silver blob notification
	useSSE({
		filter: (n) => n.type === 'docAI' && !!jobIdRef.current && n.transactionId === jobIdRef.current,
		onNotification: useCallback(
			async (notification) => {
				if (!aiExtracting) return;
				// bldt-notifications wraps the data as payload.message (parsed from the Python dict)
				const rawPayload = notification.payload || {};
				const msg =
					typeof rawPayload.message === 'object' && rawPayload.message !== null
						? (rawPayload.message as Record<string, unknown>)
						: (rawPayload as Record<string, unknown>);
				const blobName = msg.blob_name as string | undefined;
				if (!blobName) return;

				try {
					// Parse container and blob path from blob_name (e.g. "silver/doc-output.json")
					const parts = blobName.split('/');
					const container = parts[0] || 'silver';
					const blob = parts.slice(1).join('/');

					const blobResponse = await fetchAiBlob(container, blob);
					const content =
						typeof blobResponse.content === 'object'
							? (blobResponse.content as Record<string, unknown>)
							: {};

					setSilverBlobData(content);

					const parsed = parseSilverBlobContent(
						content,
						invoiceCurrency || contractCurrency || 'USD'
					);

					setAiFields(parsed.fields);
					setAiConfidence(parsed.foundCount);
					setAiExtracting(false);
					setAiExtracted(true);

					// Store AI values separately for merge UI
					const newAiValues: Record<MergeFieldKey, string> = {
						invoiceNumber: parsed.invoiceNumber || '',
						invoiceDate: parsed.invoiceDate ? toInputDate(parsed.invoiceDate) : '',
						invoiceAmount: parsed.totalAmount ? String(parsed.totalAmount) : '',
						invoiceCurrency: parsed.currency || '',
						dueDate: parsed.dueDate ? toInputDate(parsed.dueDate) : '',
					};
					setAiExtractedValues(newAiValues);

					// Auto-resolve defaults: prefer AI when available, user when AI is missing
					setFieldSources((prev) => {
						const defaults = { ...prev };
						for (const key of MERGE_FIELD_KEYS) {
							const userVal = userEnteredFields[key];
							const aiVal = newAiValues[key];
							if (userVal && aiVal && userVal !== aiVal) defaults[key] = 'ai';
							else if (userVal && !aiVal) defaults[key] = 'user';
							else if (!userVal && aiVal) defaults[key] = 'ai';
							else if (userVal && aiVal && userVal === aiVal) defaults[key] = 'ai';
						}
						return defaults;
					});

					// Set form fields to resolved values (AI take precedence for initial display)
					if (parsed.invoiceNumber) setInvoiceNumber(parsed.invoiceNumber);
					if (parsed.invoiceDate) setInvoiceDate(toInputDate(parsed.invoiceDate));
					if (parsed.totalAmount) setInvoiceAmount(String(parsed.totalAmount));
					if (parsed.currency) setInvoiceCurrency(parsed.currency);
					if (parsed.dueDate) setDueDate(toInputDate(parsed.dueDate));
				} catch (err) {
					console.error('Failed to fetch silver blob:', err);
					setAiExtracting(false);
				}
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps -- MERGE_FIELD_KEYS is a constant; userEnteredFields read via setter pattern would cause stale loops
			[aiExtracting, invoiceCurrency, contractCurrency]
		),
	});

	// Helper: check if AI found a specific field
	const aiFound = useCallback(
		(label: string) => aiFields.find((f) => f.label === label)?.found ?? null,
		[aiFields]
	);

	// Inline AI indicator for form fields
	const aiAdornment = useCallback(
		(label: string) => {
			const status = aiFound(label);
			if (status === null) return undefined; // no extraction yet
			return {
				endAdornment: (
					<InputAdornment position="end">
						<Tooltip
							title={status ? t('invoice.autoPopulated') : t('invoice.notFoundEnterManually')}>
							{status ? (
								<IconCheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
							) : (
								<IconWarning sx={{ fontSize: 16, color: 'warning.main' }} />
							)}
						</Tooltip>
					</InputAdornment>
				),
			};
		},
		[aiFound]
	);

	// Manual contract validation (when no file uploaded)
	const handleManualValidation = () => {
		const fields: ExtractedField[] = [
			{ label: 'Supplier Name', extracted: supplierName, found: !!supplierName },
			{ label: 'Document Type', extracted: '', found: false },
			{ label: 'Billing Type', extracted: '', found: false },
			{ label: 'Project', extracted: '', found: false },
			{ label: 'Contract Amount', extracted: '', found: false },
			{ label: 'Progress', extracted: '', found: false },
			{ label: 'Invoice Number', extracted: invoiceNumber || '', found: !!invoiceNumber },
			{
				label: 'Invoice Amount',
				extracted: invoiceAmount
					? `${invoiceCurrency} ${Number(invoiceAmount).toLocaleString()}`
					: '',
				found: !!invoiceAmount,
			},
			{ label: 'Invoice Date', extracted: invoiceDate || '', found: !!invoiceDate },
			{ label: 'Currency', extracted: invoiceCurrency, found: !!invoiceCurrency },
			{ label: 'Due Date', extracted: '', found: false },
			{ label: 'Summary', extracted: '', found: false },
		];
		setAiFields(fields);
		setAiConfidence(fields.filter((f) => f.found).length);
		setAiExtracted(true);
	};

	// Apply resolved values to form state when source selection changes
	const handleSourceChange = (key: MergeFieldKey, source: FieldSource) => {
		setFieldSources((prev) => ({ ...prev, [key]: source }));
		const value =
			source === 'user'
				? userEnteredFields[key]
				: source === 'ai'
					? aiExtractedValues[key]
					: customFields[key];
		switch (key) {
			case 'invoiceNumber':
				setInvoiceNumber(value);
				break;
			case 'invoiceDate':
				setInvoiceDate(value);
				break;
			case 'invoiceAmount':
				setInvoiceAmount(value);
				break;
			case 'invoiceCurrency':
				setInvoiceCurrency(value);
				break;
			case 'dueDate':
				setDueDate(value);
				break;
		}
	};

	// Handle custom field edits
	const handleCustomFieldChange = (key: MergeFieldKey, value: string) => {
		setCustomFields((prev) => ({ ...prev, [key]: value }));
		if (fieldSources[key] === 'custom') {
			switch (key) {
				case 'invoiceNumber':
					setInvoiceNumber(value);
					break;
				case 'invoiceDate':
					setInvoiceDate(value);
					break;
				case 'invoiceAmount':
					setInvoiceAmount(value);
					break;
				case 'invoiceCurrency':
					setInvoiceCurrency(value);
					break;
				case 'dueDate':
					setDueDate(value);
					break;
			}
		}
	};

	// Complete Upload: save AI extraction + resolved fields, keep status DRAFT
	const handleCompleteUpload = async () => {
		if (!contractId) return;
		setIsSubmitting(true);
		try {
			let invoiceId = createdInvoiceId;

			// If no draft exists yet (e.g. skip AI without file upload), create one
			if (!invoiceId) {
				const draftPayload: InvoicePayload = {
					contract: contractId,
					num: invoiceNumber || null,
					date: invoiceDate || null,
					dateDue: dueDate || null,
					amount: parseFloat(invoiceAmount) || null,
					currency: invoiceCurrency || contractCurrency || 'USD',
					description: invoiceDescription || null,
					status: 'DRAFT',
					source: 'UPLOAD',
					milestoneNumber: selectedMilestone || null,
					milestoneName: milestones.find((m) => m.id === selectedMilestone)?.name || null,
				};
				const createRes = await createInvoice(draftPayload);
				if (createRes?.data?.id) {
					invoiceId = createRes.data.id;
					setCreatedInvoiceId(invoiceId);
				} else {
					setBanner({
						message: createRes?.error?.message || 'Failed to create invoice.',
						severity: 'error',
					});
					return;
				}
			}

			const response = await updateInvoice(invoiceId, {
				num: invoiceNumber,
				date: invoiceDate,
				dateDue: dueDate,
				amount: parseFloat(invoiceAmount) || 0,
				currency: invoiceCurrency || contractCurrency || 'USD',
				description: invoiceDescription || null,
				milestoneNumber: selectedMilestone || null,
				milestoneName: milestones.find((m) => m.id === selectedMilestone)?.name || null,
				aiExtraction: silverBlobData
					? {
							invoiceNumber: invoiceNumber || null,
							vendorName: supplierName || null,
							invoiceDate: invoiceDate || null,
							dueDate: dueDate || null,
							totalAmount: parseFloat(invoiceAmount) || null,
							currency: invoiceCurrency || null,
							lineItems: [],
							confidence: aiConfidence / 100,
							extractedAt: new Date().toISOString(),
						}
					: {
							invoiceNumber: invoiceNumber || null,
							vendorName: supplierName || null,
							invoiceDate: invoiceDate || null,
							dueDate: dueDate || null,
							totalAmount: parseFloat(invoiceAmount) || null,
							currency: invoiceCurrency || null,
							lineItems: [],
							confidence: 0,
							extractedAt: new Date().toISOString(),
						},
				contractValidation:
					aiFields.length > 0
						? {
								contractorNameMatch:
									aiFields.find((f) => f.label === 'Supplier Name')?.found ?? true,
								amountWithinContract:
									aiFields.find((f) => f.label === 'Invoice Amount')?.found ?? true,
								paymentTermsMatch: aiFields.find((f) => f.label === 'Currency')?.found ?? true,
								isValid: aiFields.every((f) => f.found),
								validatedAt: new Date().toISOString(),
							}
						: undefined,
			});
			if (response?.error) {
				setBanner({
					message: response.error.message || 'Failed to save invoice.',
					severity: 'error',
				});
			} else {
				onSuccess?.();
				handleClose();
			}
		} catch (err) {
			setBanner({ message: 'Failed to save invoice. Please try again.', severity: 'error' });
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleClose = () => {
		setActiveStep(0);
		setInvoiceNumber('');
		setInvoiceDate('');
		setInvoiceAmount('');
		setInvoiceCurrency(contractCurrency || 'USD');
		setDueDate('');
		setSelectedMilestone('');
		setInvoiceFile(null);
		setInvoiceDescription('');
		setUploadStatus('idle');
		setUploadResult(null);
		setUploadError(null);
		setIsSaving(false);
		setAiExtracting(false);
		setAiExtracted(false);
		setAiConfidence(0);
		setAiFields([]);
		setSilverBlobData(null);
		jobIdRef.current = null;
		setIsSubmitting(false);
		setCreatedInvoiceId(null);
		setSkipAiExtraction(false);
		setUserEnteredFields({
			invoiceNumber: '',
			invoiceDate: '',
			invoiceAmount: '',
			invoiceCurrency: '',
			dueDate: '',
		});
		setAiExtractedValues({
			invoiceNumber: '',
			invoiceDate: '',
			invoiceAmount: '',
			invoiceCurrency: '',
			dueDate: '',
		});
		setFieldSources({
			invoiceNumber: 'ai',
			invoiceDate: 'ai',
			invoiceAmount: 'ai',
			invoiceCurrency: 'ai',
			dueDate: 'ai',
		});
		setCustomFields({
			invoiceNumber: '',
			invoiceDate: '',
			invoiceAmount: '',
			invoiceCurrency: '',
			dueDate: '',
		});
		onClose();
	};

	const handleCancelClick = () => {
		handleClose();
	};

	const handleAbandonDraft = async () => {
		const invoiceId = createdInvoiceId || draftInvoice?.id;
		if (!invoiceId) return;
		setIsAbandoning(true);
		try {
			await deactivateInvoice(invoiceId);
			setAbandonConfirmOpen(false);
			handleClose();
			onSuccess?.();
		} catch (err) {
			console.error('Failed to cancel invoice:', err);
			setBanner({ message: 'Failed to cancel invoice. Please try again.', severity: 'error' });
		} finally {
			setIsAbandoning(false);
		}
	};

	// =========================================================================
	// Section header helper
	// =========================================================================
	const SectionHeader = ({
		icon: Icon,
		title,
		subtitle,
		color,
	}: {
		icon: React.ElementType;
		title: string;
		subtitle?: string;
		color: string;
	}) => (
		<Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
			<Avatar sx={{ bgcolor: alpha(color, 0.12), width: 36, height: 36 }}>
				<Icon sx={{ color, fontSize: 20 }} />
			</Avatar>
			<Box>
				<Typography variant="subtitle1" fontWeight={600}>
					{title}
				</Typography>
				{subtitle && (
					<Typography variant="caption" color="text.secondary">
						{subtitle}
					</Typography>
				)}
			</Box>
		</Stack>
	);

	// =========================================================================
	// Step 1: Upload Invoice
	// =========================================================================
	const renderStep1 = () => (
		<Stack spacing={3}>
			<SectionHeader
				icon={IconInvoice}
				title={t('invoice.uploadTitle')}
				subtitle={t('invoice.uploadSubtitle')}
				color={theme.palette.primary.main}
			/>

			{/* Explanatory note */}
			<Paper
				sx={{
					p: 1.5,
					bgcolor: alpha(theme.palette.info.main, 0.04),
					border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
					borderRadius: radius.md,
				}}>
				<Stack direction="row" spacing={1} alignItems="center">
					<IconInfo sx={{ color: 'info.main', fontSize: 18 }} />
					<Typography variant="caption" color="text.secondary">
						{t('invoice.uploadHint')}
					</Typography>
				</Stack>
			</Paper>

			{/* Skip AI option */}
			<FormControlLabel
				control={
					<Checkbox
						checked={skipAiExtraction}
						onChange={(e) => setSkipAiExtraction(e.target.checked)}
						size="small"
					/>
				}
				label={
					<Typography variant="body2" color="text.secondary">
						{t('invoice.skipAiLabel')}
					</Typography>
				}
			/>

			{/* File upload — prominent, first */}
			{uploadStatus === 'success' && !invoiceFile && draftInvoice?.attachments?.length ? (
				/* Already-uploaded state (resuming a DRAFT) */
				<Paper
					variant="outlined"
					sx={{
						p: 3,
						textAlign: 'center',
						borderColor: 'success.main',
						bgcolor: alpha(theme.palette.success.main, 0.04),
					}}>
					<Stack spacing={1} alignItems="center">
						<IconCheckCircle color="success" sx={{ fontSize: 32 }} />
						<Typography variant="body2" fontWeight={600}>
							{draftInvoice.attachments[0].name}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{t('invoice.alreadyUploaded')}
						</Typography>
					</Stack>
				</Paper>
			) : (
				<Paper
					variant="outlined"
					sx={{
						p: 3,
						textAlign: 'center',
						borderStyle: 'dashed',
						borderColor: invoiceFile ? 'success.main' : 'divider',
						bgcolor: invoiceFile ? alpha(theme.palette.success.main, 0.04) : 'transparent',
						cursor: 'pointer',
						'&:hover': {
							borderColor: 'primary.main',
							bgcolor: alpha(theme.palette.primary.main, 0.02),
						},
					}}
					component="label">
					<input
						type="file"
						hidden
						accept=".pdf,.docx,.xlsx,.jpg,.png"
						onChange={handleFileSelect}
					/>
					{invoiceFile ? (
						<Stack spacing={1} alignItems="center">
							<IconCheckCircle color="success" sx={{ fontSize: 32 }} />
							<Typography variant="body2" fontWeight={600}>
								{invoiceFile.name}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{t('invoice.clickToReplace', { size: (invoiceFile.size / 1024).toFixed(1) })}
							</Typography>
						</Stack>
					) : (
						<Stack spacing={1} alignItems="center">
							<IconUpload sx={{ fontSize: 32, color: 'text.secondary' }} />
							<Typography variant="body2" fontWeight={600}>
								{t('invoice.uploadDocument')}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{t('invoice.uploadFormats')}
							</Typography>
						</Stack>
					)}
				</Paper>
			)}

			{/* Manual entry fields — always shown so user can provide values for merge in Step 2 */}
			{invoiceFile && uploadStatus !== 'success' && (
				<Paper
					sx={{
						p: 1.5,
						bgcolor: alpha(theme.palette.info.main, 0.04),
						border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
						borderRadius: radius.md,
					}}>
					<Stack direction="row" spacing={1} alignItems="center">
						<IconInfo sx={{ color: 'info.main', fontSize: 18 }} />
						<Typography variant="caption" color="text.secondary">
							Optional: enter values below. After AI extraction, you&apos;ll choose between your
							values and AI-extracted values.
						</Typography>
					</Stack>
				</Paper>
			)}

			{/* Invoice Number + Date */}
			<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
				<TextField
					label={t('invoice.invoiceNumber')}
					value={invoiceNumber}
					onChange={(e) => setInvoiceNumber(e.target.value)}
					fullWidth
					size="small"
					placeholder={t('invoice.invoiceNumberPlaceholder')}
				/>
				<TextField
					label={t('invoice.invoiceDate')}
					type="date"
					value={invoiceDate}
					onChange={(e) => setInvoiceDate(e.target.value)}
					fullWidth
					size="small"
					InputLabelProps={{ shrink: true }}
				/>
			</Box>

			{/* Amount + Currency */}
			<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
				<TextField
					label={t('invoice.invoiceAmount')}
					type="number"
					value={invoiceAmount}
					onChange={(e) => setInvoiceAmount(e.target.value)}
					fullWidth
					size="small"
					InputProps={{
						startAdornment: <InputAdornment position="start">{invoiceCurrency}</InputAdornment>,
					}}
				/>
				<FormControl fullWidth size="small">
					<InputLabel>{t('invoice.invoiceCurrency')}</InputLabel>
					<Select
						label={t('invoice.invoiceCurrency')}
						value={invoiceCurrency}
						onChange={(e) => setInvoiceCurrency(String(e.target.value))}>
						{currencies.map((c) => (
							<MenuItem key={c} value={c}>
								{c}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>

			{/* Due date */}
			<TextField
				label={t('invoice.dueDate')}
				type="date"
				value={dueDate}
				onChange={(e) => setDueDate(e.target.value)}
				fullWidth
				size="small"
				InputLabelProps={{ shrink: true }}
			/>

			{/* Milestone */}
			<FormControl fullWidth size="small" required>
				<InputLabel>{t('invoice.linkedMilestone')}</InputLabel>
				<Select
					label={t('invoice.linkedMilestone')}
					value={selectedMilestone}
					onChange={(e) => setSelectedMilestone(e.target.value as number)}>
					{milestones.map((m) => (
						<MenuItem key={m.id} value={m.id}>
							<Stack direction="row" justifyContent="space-between" width="100%">
								<span>{m.name}</span>
								<Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
									{m.percentage}% — {contractCurrency} {m.amount.toLocaleString()}
								</Typography>
							</Stack>
						</MenuItem>
					))}
				</Select>
			</FormControl>

			{/* Description */}
			<TextField
				label={t('invoice.description')}
				value={invoiceDescription}
				onChange={(e) => setInvoiceDescription(e.target.value)}
				fullWidth
				size="small"
				multiline
				rows={2}
				placeholder={t('invoice.descriptionPlaceholder')}
			/>

			{/* Contract context */}
			<Paper variant="outlined" sx={{ p: 1.5, bgcolor: alpha(theme.palette.grey[500], 0.04) }}>
				<Stack direction="row" spacing={1} alignItems="center">
					<IconInfo sx={{ color: 'text.secondary', fontSize: 18 }} />
					<Typography variant="caption" color="text.secondary">
						Supplier: <strong>{supplierName || 'N/A'}</strong> &bull; Contract:{' '}
						<strong>{contractNum}</strong> &bull; Contract Value:{' '}
						<strong>
							{contractCurrency} {contractAmount.toLocaleString()}
						</strong>
					</Typography>
				</Stack>
			</Paper>
		</Stack>
	);

	// =========================================================================
	// Step 2: AI Extraction & Merge
	// =========================================================================

	// Whether the user entered any values in Step 1 (to show merge UI)
	const hasUserValues = Object.values(userEnteredFields).some((v) => v.length > 0);
	const hasAiValues = Object.values(aiExtractedValues).some((v) => v.length > 0);
	const showMergeUI = hasUserValues && hasAiValues;

	const FIELD_LABELS: Record<MergeFieldKey, string> = {
		invoiceNumber: 'Invoice Number',
		invoiceDate: 'Invoice Date',
		invoiceAmount: 'Invoice Amount',
		invoiceCurrency: 'Currency',
		dueDate: 'Due Date',
	};

	const renderMergeField = (key: MergeFieldKey) => {
		const userVal = userEnteredFields[key];
		const aiVal = aiExtractedValues[key];
		const hasConflict = userVal && aiVal && userVal !== aiVal;

		// If only one side has a value and no conflict, show simple display
		if (!hasConflict && !showMergeUI) return null;

		return (
			<Paper
				key={key}
				variant="outlined"
				sx={{
					p: 2,
					borderRadius: radius.md,
					borderColor: hasConflict ? alpha(theme.palette.warning.main, 0.4) : 'divider',
				}}>
				<Typography
					variant="caption"
					fontWeight={600}
					color="text.secondary"
					sx={{ mb: 1, display: 'block' }}>
					{FIELD_LABELS[key]}
					{hasConflict && (
						<Chip
							label={t('invoice.differsLabel')}
							size="small"
							color="warning"
							variant="outlined"
							sx={{ ml: 1, height: 18, fontSize: 10 }}
						/>
					)}
				</Typography>
				<RadioGroup
					value={fieldSources[key]}
					onChange={(e) => handleSourceChange(key, e.target.value as FieldSource)}>
					{userVal ? (
						<FormControlLabel
							value="user"
							control={<Radio size="small" />}
							label={
								<Typography variant="body2">
									Your value:{' '}
									<strong>
										{key === 'invoiceAmount'
											? `${invoiceCurrency} ${Number(userVal).toLocaleString()}`
											: userVal}
									</strong>
								</Typography>
							}
						/>
					) : null}
					{aiVal ? (
						<FormControlLabel
							value="ai"
							control={<Radio size="small" />}
							label={
								<Stack direction="row" spacing={0.5} alignItems="center">
									<Typography variant="body2">
										AI value:{' '}
										<strong>
											{key === 'invoiceAmount'
												? `${aiExtractedValues.invoiceCurrency || invoiceCurrency} ${Number(aiVal).toLocaleString()}`
												: aiVal}
										</strong>
									</Typography>
									<IconAI sx={{ fontSize: 14, color: '#7c3aed' }} />
								</Stack>
							}
						/>
					) : null}
					<FormControlLabel
						value="custom"
						control={<Radio size="small" />}
						label={
							<Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
								<Typography variant="body2" sx={{ minWidth: 56 }}>
									Custom:
								</Typography>
								<TextField
									size="small"
									variant="standard"
									type={
										key === 'invoiceDate' || key === 'dueDate'
											? 'date'
											: key === 'invoiceAmount'
												? 'number'
												: 'text'
									}
									value={customFields[key]}
									onChange={(e) => handleCustomFieldChange(key, e.target.value)}
									onFocus={() => setFieldSources((prev) => ({ ...prev, [key]: 'custom' }))}
									placeholder={`Enter ${FIELD_LABELS[key].toLowerCase()}`}
									sx={{ flex: 1 }}
									InputLabelProps={
										key === 'invoiceDate' || key === 'dueDate' ? { shrink: true } : undefined
									}
								/>
							</Stack>
						}
					/>
				</RadioGroup>
			</Paper>
		);
	};

	const renderStep2 = () => (
		<Stack spacing={3}>
			<SectionHeader
				icon={IconAI}
				title={t('invoice.aiTitle')}
				subtitle={showMergeUI ? t('invoice.aiSubtitleCompare') : t('invoice.aiSubtitleDefault')}
				color="#7c3aed"
			/>

			{/* Status — ready / extracting / error */}
			{!aiExtracted && !aiExtracting && (
				<Paper
					sx={{
						p: 2.5,
						bgcolor: alpha('#7c3aed', 0.04),
						border: `1px solid ${alpha('#7c3aed', 0.15)}`,
						borderRadius: radius.md,
						textAlign: 'center',
					}}>
					<Stack spacing={2} alignItems="center">
						<Avatar sx={{ bgcolor: alpha('#7c3aed', 0.12), width: 48, height: 48 }}>
							<IconAI sx={{ color: '#7c3aed', fontSize: 28 }} />
						</Avatar>
						<Box>
							<Typography variant="body1" fontWeight={600}>
								{t('invoice.readyToExtract')}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{t('invoice.analyzePrefix')}{' '}
								<strong>{invoiceFile?.name || t('invoice.theUploadedInvoice')}</strong>
								{t('invoice.analyzeSuffix', { num: contractNum })}
							</Typography>
						</Box>
						<Button
							variant="contained"
							startIcon={<IconAI />}
							onClick={uploadResult ? undefined : handleManualValidation}
							disabled={!!uploadResult}
							sx={{
								borderRadius: radius.md,
								textTransform: 'none',
								fontWeight: 600,
								bgcolor: '#7c3aed',
								'&:hover': { bgcolor: '#6d28d9' },
							}}>
							{uploadResult ? t('invoice.waitingForAi') : t('invoice.validateAgainstContract')}
						</Button>
					</Stack>
				</Paper>
			)}

			{aiExtracting && (
				<Paper
					sx={{
						p: 3,
						bgcolor: alpha('#7c3aed', 0.04),
						border: `1px solid ${alpha('#7c3aed', 0.15)}`,
						borderRadius: radius.md,
						textAlign: 'center',
					}}>
					<Stack spacing={1.5} alignItems="center">
						<CircularProgress size={32} sx={{ color: '#7c3aed' }} />
						<Typography variant="body2" fontWeight={600}>
							{t('invoice.analyzingInvoice')}
						</Typography>
					</Stack>
				</Paper>
			)}

			{uploadStatus === 'error' && uploadError && (
				<Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5 }}>
					<IconWarning sx={{ color: 'warning.main', fontSize: 16 }} />
					<Typography variant="caption" color="text.secondary">
						{t('invoice.aiExtractionUnavailable')}
					</Typography>
				</Stack>
			)}

			{/* AI Analysis summary card */}
			{aiExtracted && (
				<Paper variant="outlined" sx={{ borderRadius: radius.md, overflow: 'hidden' }}>
					<Box
						sx={{
							px: 2,
							py: 1,
							bgcolor: alpha('#7c3aed', 0.04),
							borderBottom: '1px solid',
							borderColor: 'divider',
						}}>
						<Stack direction="row" spacing={1} alignItems="center">
							<Tooltip title={t('invoice.aiAnalysisTooltip')}>
								<IconAI sx={{ fontSize: 16, color: '#7c3aed' }} />
							</Tooltip>
							<Typography variant="caption" fontWeight={600} color="text.secondary">
								{t('invoice.aiAnalysisLabel', { found: aiConfidence })}
							</Typography>
						</Stack>
					</Box>
					<Box sx={{ px: 2, py: 1.5 }}>
						<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
							{aiFields
								.filter((f) =>
									[
										'Supplier Name',
										'Document Type',
										'Billing Type',
										'Project',
										'Contract Amount',
										'Progress',
									].includes(f.label)
								)
								.map((field) => (
									<Stack
										key={field.label}
										direction="row"
										spacing={0.5}
										alignItems="center"
										sx={{ cursor: 'default' }}>
										{field.found ? (
											<IconCheckCircle
												sx={{ fontSize: 13, color: 'success.main', flexShrink: 0 }}
											/>
										) : (
											<IconWarning sx={{ fontSize: 13, color: 'warning.main', flexShrink: 0 }} />
										)}
										<Typography
											variant="caption"
											color="text.secondary"
											noWrap
											sx={{ fontSize: 11 }}>
											{field.label}: <strong>{field.found ? field.extracted : '—'}</strong>
										</Typography>
									</Stack>
								))}
						</Box>
						{aiFound('Summary') && (
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ mt: 1, display: 'block', fontStyle: 'italic', fontSize: 11 }}>
								{aiFields.find((f) => f.label === 'Summary')?.extracted}
							</Typography>
						)}
					</Box>
				</Paper>
			)}

			{/* Merge UI — shown when both user AND AI have values */}
			{showMergeUI && aiExtracted && (
				<>
					<Paper
						sx={{
							p: 1.5,
							bgcolor: alpha(theme.palette.info.main, 0.04),
							border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
							borderRadius: radius.md,
						}}>
						<Stack direction="row" spacing={1} alignItems="center">
							<IconInfo sx={{ color: 'info.main', fontSize: 18 }} />
							<Typography variant="caption" color="text.secondary">
								You entered values in Step 1 that differ from AI extraction. Choose which to keep
								for each field below.
							</Typography>
						</Stack>
					</Paper>
					{MERGE_FIELD_KEYS.map((key) => renderMergeField(key))}
				</>
			)}

			{/* Standard form fields — shown when no merge is needed */}
			{(!showMergeUI || !aiExtracted) && (
				<Paper variant="outlined" sx={{ p: 2.5, borderRadius: radius.md }}>
					<Stack spacing={2}>
						<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
							<TextField
								label={t('invoice.invoiceNumber')}
								value={invoiceNumber}
								onChange={(e) => setInvoiceNumber(e.target.value)}
								required
								fullWidth
								size="small"
								placeholder="e.g. INV-2026-001"
								InputProps={aiAdornment('Invoice Number')}
							/>
							<TextField
								label={t('invoice.invoiceDate')}
								type="date"
								value={invoiceDate}
								onChange={(e) => setInvoiceDate(e.target.value)}
								required
								fullWidth
								size="small"
								InputLabelProps={{ shrink: true }}
								InputProps={aiAdornment('Invoice Date')}
							/>
						</Box>
						<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
							<TextField
								label={t('invoice.invoiceAmount')}
								type="number"
								value={invoiceAmount}
								onChange={(e) => setInvoiceAmount(e.target.value)}
								required
								fullWidth
								size="small"
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">{invoiceCurrency}</InputAdornment>
									),
									...aiAdornment('Invoice Amount'),
								}}
							/>
							<FormControl fullWidth size="small">
								<InputLabel>{t('invoice.invoiceCurrency')}</InputLabel>
								<Select
									label={t('invoice.invoiceCurrency')}
									value={invoiceCurrency}
									onChange={(e) => setInvoiceCurrency(String(e.target.value))}>
									{currencies.map((c) => (
										<MenuItem key={c} value={c}>
											{c}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
						<TextField
							label={t('invoice.dueDate')}
							type="date"
							value={dueDate}
							onChange={(e) => setDueDate(e.target.value)}
							fullWidth
							size="small"
							InputLabelProps={{ shrink: true }}
							InputProps={aiAdornment('Due Date')}
						/>
					</Stack>
				</Paper>
			)}
		</Stack>
	);

	// =========================================================================
	// Step content router
	// =========================================================================
	const renderStepContent = () => {
		switch (activeStep) {
			case 0:
				return renderStep1();
			case 1:
				return renderStep2();
			default:
				return null;
		}
	};

	const canAdvance = () => {
		switch (activeStep) {
			case 0:
				return isStep1Valid;
			case 1:
				return isStep2Valid;
			default:
				return false;
		}
	};

	// =========================================================================
	// Render
	// =========================================================================
	return (
		<>
			<Dialog
				open={isOpen}
				onClose={handleCancelClick}
				maxWidth="md"
				fullWidth
				PaperProps={{ sx: { borderRadius: radius.lg, maxHeight: '90vh' } }}>
				{/* Title bar */}
				<Box
					sx={{
						px: 3,
						py: 2,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						borderBottom: `1px solid ${theme.palette.divider}`,
					}}>
					<Stack direction="row" spacing={1.5} alignItems="center">
						<Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), width: 36, height: 36 }}>
							<IconInvoice sx={{ color: 'primary.main', fontSize: 20 }} />
						</Avatar>
						<Box>
							<Typography variant="h6" fontWeight={700}>
								{editMode ? t('invoice.editInvoice') : t('invoice.addInvoice')}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{t('invoice.contractHeader', { num: contractNum, supplier: supplierName })}
							</Typography>
						</Box>
					</Stack>
					<IconButton onClick={handleCancelClick} size="small">
						<IconClose />
					</IconButton>
				</Box>

				{/* Stepper */}
				<Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
					<Stepper activeStep={activeStep} alternativeLabel>
						{STEP_KEYS.map((key, index) => (
							<Step key={key} completed={index < activeStep}>
								<StepLabel>{t(key as any)}</StepLabel>
							</Step>
						))}
					</Stepper>
				</Box>

				{/* Content */}
				<DialogContent sx={{ px: 3, py: 2.5, minHeight: 400 }}>{renderStepContent()}</DialogContent>

				{/* Footer actions */}
				<DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
					{(createdInvoiceId || draftInvoice?.id) &&
						(!editMode || draftInvoice?.status === 'DRAFT') && (
							<Button
								variant="outlined"
								color="error"
								startIcon={<IconDelete />}
								onClick={() => setAbandonConfirmOpen(true)}
								sx={{
									borderRadius: radius.md,
									textTransform: 'none',
									fontWeight: 600,
									mr: 'auto',
								}}>
								{t('invoice.cancelInvoice')}
							</Button>
						)}

					{activeStep > 0 &&
						!(
							(createdInvoiceId || draftInvoice?.id) &&
							(!editMode || draftInvoice?.status === 'DRAFT')
						) && (
							<Button
								startIcon={<IconBack />}
								onClick={handleBack}
								sx={{ borderRadius: radius.md, textTransform: 'none', mr: 'auto' }}>
								{t('back')}
							</Button>
						)}

					{activeStep > 0 &&
						(createdInvoiceId || draftInvoice?.id) &&
						(!editMode || draftInvoice?.status === 'DRAFT') && (
							<Button
								startIcon={<IconBack />}
								onClick={handleBack}
								sx={{ borderRadius: radius.md, textTransform: 'none' }}>
								{t('back')}
							</Button>
						)}

					<Button
						onClick={handleCancelClick}
						sx={{ borderRadius: radius.md, textTransform: 'none' }}>
						{t('cancel')}
					</Button>

					{activeStep === 0 && (
						<Button
							variant="contained"
							endIcon={
								isSaving ? (
									<CircularProgress size={16} color="inherit" />
								) : skipAiExtraction ? (
									<IconCheckCircle />
								) : (
									<IconNext />
								)
							}
							onClick={
								skipAiExtraction
									? handleCompleteUpload
									: invoiceFile && !createdInvoiceId
										? handleUploadFile
										: handleNext
							}
							disabled={!canAdvance() || isSaving}
							sx={{ borderRadius: radius.md, textTransform: 'none', fontWeight: 600 }}>
							{isSaving
								? t('invoice.saving')
								: skipAiExtraction
									? t('invoice.saveInvoice')
									: createdInvoiceId
										? t('invoice.continue')
										: invoiceFile
											? t('invoice.uploadAndContinue')
											: t('invoice.continueWithoutUpload')}
						</Button>
					)}

					{activeStep === 1 && (
						<Button
							variant="contained"
							color="primary"
							endIcon={
								isSaving ? <CircularProgress size={16} color="inherit" /> : <IconCheckCircle />
							}
							onClick={handleCompleteUpload}
							disabled={!canAdvance() || isSaving}
							sx={{ borderRadius: radius.md, textTransform: 'none', fontWeight: 600 }}>
							{isSaving ? t('invoice.saving') : t('invoice.completeUpload')}
						</Button>
					)}
				</DialogActions>
			</Dialog>

			{/* Cancel Invoice Confirmation Dialog */}
			<Dialog
				open={abandonConfirmOpen}
				onClose={() => setAbandonConfirmOpen(false)}
				maxWidth="xs"
				fullWidth>
				<DialogTitle sx={{ fontWeight: 700 }}>{t('invoice.cancelInvoiceTitle')}</DialogTitle>
				<DialogContentText sx={{ px: 3, pb: 1 }}>
					This will permanently delete this invoice and all associated data. This action cannot be
					undone.
				</DialogContentText>
				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button
						onClick={() => setAbandonConfirmOpen(false)}
						disabled={isAbandoning}
						sx={{ borderRadius: radius.md, textTransform: 'none' }}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="error"
						onClick={handleAbandonDraft}
						disabled={isAbandoning}
						startIcon={
							isAbandoning ? <CircularProgress size={16} color="inherit" /> : <IconDelete />
						}
						sx={{ borderRadius: radius.md, textTransform: 'none', fontWeight: 600 }}>
						{isAbandoning ? t('invoice.cancelling') : t('invoice.cancelInvoice')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
